import React, { useContext } from 'react';
import { MainViewContext } from '../../../Pages/MainComponents/MainView/MainViewContext';
import { MainViewWorkflow } from '../../../Pages/MainComponents/MainView/MainViewModel';
import CustomDialog from '../CustomDialog';
import DataCollection from '../DataCollection/DataCollection';
import DeleteAccount from '../DeleteAccount/DeleteAccount';
import { IDialogProps } from '../DialogUtils';
import LanguageSelectionDialog from '../LanguageSelection/LanguageSelectionDialog';
import LegalDialog from '../Legal/LegalDialog';
import { LegalInfoType } from '../Legal/LegalDialogModel';
import RedoEasyFitDialog from '../RedoEasyFit/Dialog/RedoEasyFitDialog';
import RedoHLAADialog from '../RedoHLAA/Dialog/RedoHLAADialog';
import RedoHLAAPage from '../RedoHLAA/RedoHLAAPage';
import RedoPairingDialog from '../RedoPairing/RedoPairingDialog';
import { MenuContext } from './MenuContext';
import useMenuDialogHandler from './MenuDialogHandler';
import { MenuDialogType } from './MenuDialogType';
import useHearingAidsMenuResource from './MenuPages/HearingAids/HearingAidsMenuResourceHook';
import { useInstructionsMenuResource } from './MenuPages/InstructionsMenu/InstructionsMenuResourceHook';
import useLegalInformationMenuResource from './MenuPages/LegalInfo/LegalInfoMenuResourceHook';
import MainMenu from './MenuPages/MainMenu/MainMenu';
import useAccountSettingsResource from './MenuPages/Settings/AccountSettingsMenuResourceHook';
import useSettingsMenuResource from './MenuPages/Settings/SettingsMenuResourceHook';
import SubMenuDialog from './SubMenuDialog';

function MenuDialog({ displayed, onClose }: IDialogProps): React.ReactElement {
    const { popMenu, peekMenu, pushMenu } = useMenuDialogHandler();

    const viewContext = useContext(MainViewContext);
    const hearingAidsMenuResource = () =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useHearingAidsMenuResource({ onClose });
    const menuView: { [key: string]: React.ReactElement } = {
        main: <MainMenu onClose={onClose} key={'main'} />,
        [MenuDialogType.HearingAids]: (
            <SubMenuDialog
                useResource={hearingAidsMenuResource}
                onClose={onClose}
                key={MenuDialogType.HearingAids}
            />
        ),
        [MenuDialogType.RedoEasyFit]: (
            <RedoEasyFitDialog
                onClose={popMenu}
                key={MenuDialogType.RedoEasyFit}
            />
        ),
        [MenuDialogType.RedoPairing]: (
            <RedoPairingDialog
                onClose={popMenu}
                key={MenuDialogType.RedoPairing}
            />
        ),
        [MenuDialogType.RedoHLAA]:
            viewContext.workflowType === MainViewWorkflow.AppFitting ? (
                <RedoHLAAPage onClose={onClose} />
            ) : (
                <RedoHLAADialog
                    onClose={onClose}
                    key={MenuDialogType.RedoHLAA}
                />
            ),
        [MenuDialogType.Instructions]: (
            <SubMenuDialog
                useResource={useInstructionsMenuResource}
                onClose={onClose}
                key={MenuDialogType.Instructions}
            />
        ),
        [MenuDialogType.Settings]: (
            <SubMenuDialog
                useResource={useSettingsMenuResource}
                onClose={onClose}
                key={MenuDialogType.Settings}
            />
        ),
        [MenuDialogType.AccountSettings]: (
            <SubMenuDialog
                key={MenuDialogType.AccountSettings}
                useResource={useAccountSettingsResource}
                onClose={onClose}
            />
        ),
        [MenuDialogType.RevokeAnalyticConsent]: (
            <DataCollection
                key={MenuDialogType.RevokeAnalyticConsent}
                onClose={onClose}
            />
        ),
        [MenuDialogType.RevokeOperationConsent]: (
            <DeleteAccount
                onClose={onClose}
                key={MenuDialogType.RevokeOperationConsent}
            />
        ),
        [MenuDialogType.LanguageSelection]: (
            <LanguageSelectionDialog
                onClose={onClose}
                key={MenuDialogType.LanguageSelection}
            />
        ),
        [MenuDialogType.LegalInformation]: (
            <SubMenuDialog
                useResource={useLegalInformationMenuResource}
                onClose={onClose}
                key={MenuDialogType.LegalInformation}
            />
        ),
        [MenuDialogType.AppInfo]: (
            <LegalDialog
                type={LegalInfoType.AppInfo}
                onClose={popMenu}
                key={MenuDialogType.AppInfo}
            />
        ),
        [MenuDialogType.LegalInfo]: (
            <LegalDialog
                type={LegalInfoType.LegalInfo}
                onClose={popMenu}
                key={MenuDialogType.LegalInfo}
            />
        ),
    };

    return (
        <MenuContext.Provider value={{ pushMenu, popMenu, onClose }}>
            <CustomDialog open={displayed} onClose={onClose}>
                {menuView[peekMenu()]}
            </CustomDialog>
        </MenuContext.Provider>
    );
}
export default React.memo(MenuDialog);
